<script setup>
import world from '@/misc/world.json'
import nationalities from '@/misc/nationalities.json'
import {computed} from "vue";
import {useI18n} from "vue-i18n";
import {isEmpty} from "@/utils";

const props = defineProps({
  title: String,
  placeholder: String,
  id: String,
  modelValue: String,
  required: Boolean,
  disabled: Boolean,
  // Zeigt die Staatsangehörigkeit als Eigenschaftswort ("deutsch") statt als
  // Land ("Deutschland") — gefragt ist ja die Person, nicht ihr Wohnort.
  nationality: Boolean,
})
const emits = defineEmits(['update:modelValue'])

const i18n = useI18n()

const value = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emits('update:modelValue', value)
  }
})

const byLabel = locale => (a, b) => a.label.localeCompare(b.label, locale)

const options = computed(() => {
  const locale = i18n.locale.value
  const fallback = i18n.fallbackLocale.value
  const name = country => country[locale] ?? country[fallback]

  if (!props.nationality) {
    return [{
      items: world
          .map(country => ({code: country.alpha2.toUpperCase(), label: name(country)}))
          .sort(byLabel(locale)),
    }]
  }

  // Das Eigenschaftswort ("deutsch") gibt es nur für die Staatsangehörigkeiten
  // in nationalities.json. Der Rest bleibt beim Landesnamen — ein korrekter
  // Landesname ist besser als ein geratenes Adjektiv. Damit die Liste nicht
  // beides wahllos mischt, stehen sie in getrennten Gruppen.
  const known = []
  const rest = []

  world.forEach(country => {
    const code = country.alpha2.toUpperCase()
    const demonym = nationalities[code]?.[locale] ?? nationalities[code]?.[fallback]

    if (demonym) {
      known.push({code, label: demonym})
    } else {
      rest.push({code, label: name(country)})
    }
  })

  return [
    {label: 'nationalityCommon', items: known.sort(byLabel(locale))},
    {label: 'nationalityOther', items: rest.sort(byLabel(locale))},
  ]
})
</script>

<template>
  <label class="ww-field" :for="id">
    <span class="ww-field__label">{{ $t(props.title) }}</span>
    <select
        class="ww-input"
        :id="id"
        :aria-invalid="isEmpty(value) ? undefined : !value"
        v-model="value"

        :required="props.required"
        :disabled="props.disabled"
    >
      <option value="" selected>{{ $t(props.placeholder) }}</option>
      <template v-for="(group, index) in options" :key="index">
        <optgroup v-if="group.label" :label="$t(group.label)">
          <option v-for="item in group.items" :key="item.code" :value="item.code">{{ item.label }}</option>
        </optgroup>
        <template v-else>
          <option v-for="item in group.items" :key="item.code" :value="item.code">{{ item.label }}</option>
        </template>
      </template>
    </select>
  </label>
</template>
